import Alpine from 'alpinejs'
import type { Alpine as AlpineType } from 'alpinejs'

import type { NotificationTeamKey } from './onesignal'
import {
  applyDefaultTeamCalendarNav,
  emptyNotifications,
  getDefaultTeam,
  PWA_ONBOARDING_KEY,
  type PwaPrefsStore,
} from './pwa-prefs'
import { registerServiceWorker } from './sw-register'

// Re-export for consumers that already import from pwa.ts
export { getDefaultTeam, PWA_DEFAULT_TEAM_KEY, PWA_ONBOARDING_KEY } from './pwa-prefs'

declare global {
  interface Window {
    Alpine: AlpineType
  }
}

const prefs = (): PwaPrefsStore => Alpine.store('pwaPrefs') as PwaPrefsStore

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * True only when running as the installed app.
 *
 * Deliberately narrow: site.webmanifest declares `display: standalone` with no
 * `display_override`, so an installed LHC app can only ever report `standalone`
 * (plus `navigator.standalone` on iOS). Matching `minimal-ui` / `fullscreen` here
 * would only add false positives — a desktop browser in F11 / macOS fullscreen, or
 * an Android in-app WebView, reports those without anything being installed.
 *
 * Keep in sync with the media queries in src/styles/pwa.css.
 */
const isInstalled = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  Boolean((navigator as Navigator & { standalone?: boolean }).standalone)

const isAndroid = () => /Android/i.test(navigator.userAgent)

const isMobile = () => /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)

// ---------------------------------------------------------------------------
// Install banner (Alpine store)
// ---------------------------------------------------------------------------

const PWA_BANNER_DISMISS_KEY = 'lhc-pwa-install-banner-dismissed'

type InstallPromptEvent = Event & {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: string }>
}

type PwaInstallBannerStore = {
  visible: boolean
  isAndroid: boolean
  _installPrompt: InstallPromptEvent | null
  init(): void
  dismiss(): void
  show(): void
  install(instructionsUrl?: string): Promise<void>
}

export const registerPwaInstallBannerStore = (AlpineInstance: AlpineType): void => {
  AlpineInstance.store('pwaInstallBanner', {
    visible: false,
    isAndroid: isAndroid(),
    _installPrompt: null,

    init(this: PwaInstallBannerStore) {
      // Always capture the native install prompt (desktop Chromium fires it too),
      // so the homepage CTA can trigger it even though the mobile banner below stays mobile-only.
      window.addEventListener('beforeinstallprompt', (event) => {
        event.preventDefault()
        this._installPrompt = event as InstallPromptEvent
        if (isMobile()) this.show()
      })

      if (isInstalled() || sessionStorage.getItem(PWA_BANNER_DISMISS_KEY) || !isMobile()) return

      if (!isAndroid()) {
        this.show()
      }
    },

    dismiss(this: PwaInstallBannerStore) {
      sessionStorage.setItem(PWA_BANNER_DISMISS_KEY, '1')
      this.visible = false
    },

    show(this: PwaInstallBannerStore) {
      if (isInstalled() || sessionStorage.getItem(PWA_BANNER_DISMISS_KEY)) return
      this.visible = true
    },

    async install(this: PwaInstallBannerStore, instructionsUrl?: string) {
      if (this._installPrompt) {
        await this._installPrompt.prompt()
        if ((await this._installPrompt.userChoice).outcome === 'accepted') {
          this.dismiss()
        }
        this._installPrompt = null
        return
      }

      if (instructionsUrl) {
        window.location.href = instructionsUrl
      }
    },
  })
}

// ---------------------------------------------------------------------------
// Onboarding (Alpine component)
// ---------------------------------------------------------------------------

export const PwaOnboarding = () => ({
  storageKey: PWA_ONBOARDING_KEY,
  // DEV: always show on load — set visible: true in init()
  visible: false,
  step: 1 as 1 | 2,
  selectedTeam: null as NotificationTeamKey | null,
  syncing: false,

  init() {
    // First open after PWA install only (persisted across relaunches)
    // DEV: this.visible = true
    this.visible = isInstalled() && !localStorage.getItem(this.storageKey)

    // Only clear the draft when the onboarding actually runs — this component is
    // mounted on every page, and resetting unconditionally would wipe the
    // preferences the settings page just loaded from OneSignal.
    if (this.visible) prefs().notifications = emptyNotifications()
  },

  selectTeam(id: NotificationTeamKey) {
    this.selectedTeam = id
  },

  confirmStep1() {
    if (!this.selectedTeam) return
    prefs().notifications = {
      ...prefs().notifications,
      [this.selectedTeam]: true,
      club: true,
    }
    this.step = 2
  },

  toggleNotification(key: NotificationTeamKey) {
    prefs().toggleNotificationDraft(key)
  },

  async complete() {
    // DEV: comment the next line so onboarding reappears on reload
    localStorage.setItem(this.storageKey, '1')
    this.visible = false

    if (this.selectedTeam) {
      prefs().setDefaultTeam(this.selectedTeam)
    }

    this.syncing = true
    try {
      await prefs().syncNotifications()
    } catch (error) {
      console.error('OneSignal sync failed:', error)
    } finally {
      this.syncing = false
    }
  },

  /**
   * Close without saving anything.
   *
   * Must never go through complete(): syncing a draft with every team off calls
   * removeTags() on all four. An already-subscribed user who sees the onboarding
   * again (localStorage cleared, `appinstalled` re-fired) would be unsubscribed
   * just by closing it.
   */
  dismiss() {
    localStorage.setItem(this.storageKey, '1')
    this.visible = false
  },
})

// ---------------------------------------------------------------------------
// Settings (Alpine component — init only; UI binds to $store.pwaPrefs)
// ---------------------------------------------------------------------------

export const PwaSettings = () => ({
  async init() {
    prefs().loadDefaultTeam()
    await prefs().loadNotifications()
  },
})

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

/** Chrome keeps localStorage after PWA uninstall — reset onboarding on (re)install. */
const initOnboardingInstallReset = (): void => {
  window.addEventListener('appinstalled', () => {
    localStorage.removeItem(PWA_ONBOARDING_KEY)
  })
}

let initialized = false

export const initPwa = (): void => {
  if (initialized) return
  initialized = true

  void registerServiceWorker()

  const banner = Alpine.store('pwaInstallBanner') as PwaInstallBannerStore | undefined
  banner?.init()

  initOnboardingInstallReset()
  applyDefaultTeamCalendarNav()
}
